/* eslint-disable no-unused-vars */
/* eslint-disable import/no-named-default */
/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Authenticator,
} from '@aws-amplify/ui-react';
import { Auth, I18n } from 'aws-amplify';
import { motion } from 'framer-motion';
import { DevicePhoneMobileIcon, WifiIcon, LinkIcon as HeroLinkIcon, SparklesIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';
import {
  useLocation,
} from 'react-router-dom';
import LoginButton from '../components/utils/LoginButton';
import FallingSnow from '../components/FallingSnow';
import { default as logo } from '../assets/portal_logo.svg';
import { target } from '../config/awsExports';
import Maintenance from './Maintenance';
import useConfig from '../hooks/useGetConfig';
import useChristmasSeason from '../hooks/useChristmasSeason';
import { targetName } from '../utils/portalEnvs';
import getCookieValue from '../utils/getCookieValue';
const SSO_ENABLED_COOKIE = 'ssoEnabled'; // Define cookie name

const StyledAuthenticator = () => {
  I18n.putVocabulariesForLanguage('en', {
    'Sign In': 'Log in with Amazon',
  });
  const formFields = {
    signIn: {
      username: {
        placeholder: 'Username',
        isRequired: true,
        labelHidden: true
      },
      password: {
        placeholder: 'Password',
        isRequired: true,
        labelHidden: true,
      },
    },
  };

  // Applying Tailwind classes directly for Authenticator styling
  return (
    <div className="[&_.amplify-button--primary]:bg-blue-500 [&_.amplify-button--link]:text-blue-500 [&_.amplify-button:hover]:bg-blue-600 [&_.amplify-button:hover]:text-white [&_.amplify-button:hover]:border-blue-600 [&_.amplify-button:focus]:bg-gray-100 [&_.amplify-button:focus]:text-blue-500 [&_.amplify-button:focus]:border-blue-500 [&_.amplify-textfield]:text-gray-700 [&_[data-amplify-router]]:bg-white/80 [&_[data-amplify-router]]:rounded-lg [&_[data-amplify-router-content]]:rounded-lg [&_[data-amplify-router-content]]:border-none">
      < Authenticator hideSignUp formFields={formFields} />
    </div >
  );
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};



export default function Login() {
  const location = useLocation();
  const [isInMaintenance, setIsInMaintenance] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const routeContainsPortal = window.location.href.includes('portal');
  const newUrlPortal = target === 'STAG' ? `https://portal.stag.niotp.com/#${location.pathname}` : `https://portal.niotp.com/#${location.pathname}`;
  const { configData } = useConfig();
  const cookieRootAccess = getCookieValue('rootAccess') === 'true';
  const isChristmasSeason = useChristmasSeason();
  // Default to true for Christmas testing if config is not loaded yet
  const flagDaysChristmas = configData?.FlagDaysChristmas !== 'false';
  const isChristmasActive = flagDaysChristmas && isChristmasSeason;

  // Debug logging
  React.useEffect(() => {
    console.log('Christmas Debug:', {
      isChristmasSeason,
      flagDaysChristmas,
      configData: configData?.FlagDaysChristmas,
      isChristmasActive
    });
  }, [isChristmasSeason, flagDaysChristmas, configData, isChristmasActive]);

  useEffect(() => {
    // if target is not DEv and contains portal redirect to the new url Portal
    if (target === 'DEVS' && (!routeContainsPortal)) {
      window.location.href = newUrlPortal;
    }
  }, [routeContainsPortal, newUrlPortal]);

  useEffect(() => {
    setIsInMaintenance(configData?.maintenance === 'true');
  }, [configData]);

  // Reset loading state when user navigates away (e.g., after successful auth)
  useEffect(() => {
    return () => {
      setIsLoading(false);
    };
  }, []);

  const titleName = `Consumer IoT Portal (${targetName[target]})`;

  const getProvider = () => {
    let ssoCookieValue = getCookieValue(SSO_ENABLED_COOKIE);
    if (ssoCookieValue === 'true') {
      if (target === 'STAG' || target === 'DEV') {
        return 'Nestle';
      }
      return 'NestleSSO';
    }
    return undefined;
  };

  const isLocalHostDevOrTest = window.location.hostname === 'localhost' || target === 'DEV' || window.playwrightTest === true || localStorage.getItem('isPlaywrightTest') === 'true';


  const handleLogin = async () => { // Make handleLogin async
    const provider = getProvider();

    try {
      setIsLoading(true);
      // Moved Auth.federatedSignIn inside the loop
      await Auth.federatedSignIn({ provider });
    }
    catch (error) {
      console.error('Error during federated sign-in:', error);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[100vh] relative bg-white flex flex-col scrollbar scrollbar-thumb-orange-500 scrollbar-track-gray-100 overflow-hidden">
      {/* Falling Snow Effect - visible only during Christmas season and before login */}
      <FallingSnow isActive={isChristmasActive} />
      {(isInMaintenance && !cookieRootAccess)
        ? <Maintenance className="flex flex-col w-screen h-screen mb-5 bg-cover max-w-full" style={{ background: 'linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9))' }} />
        : (
          <div className="bg-white relative overflow-hidden">
            <div className="flex items-center flex-row px-4 py-2">
              <div className="mr-2">
                <img src={logo} alt="Logo" className="transform scale-90 opacity-90" />
              </div>
              <div>
                <p className="text-base text-black pl-1">
                  {titleName}
                </p>
              </div>
            </div>

            <motion.div
              className="flex flex-col w-screen min-h-0 mb-0 bg-cover max-w-full"
              style={{
                backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)))',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center center',
              }}
            >
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="page-container"
              >
                <div className="flex flex-col justify-center items-center content-center mt-8 mb-6">
                  <p 
                    className="text-4xl text-center text-gray-900 flex items-center justify-center gap-2 tracking-tight"                  
                  >
                    {isChristmasActive && (
                      <span className="text-4xl flex-shrink-0" aria-hidden="true">🎄</span>
                    )}
                    <span className="font-normal">Welcome to </span><span className="font-extrabold bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">IoT Portal</span>
                    {isChristmasActive && (
                      <span className="text-4xl flex-shrink-0" aria-hidden="true">🎄</span>
                    )}
                  </p>
                  <motion.div variants={itemVariants}>
                    <div className="p-1">
                      <p className="font-light text-md text-center text-black px-2 py-1 sm:text-base">
                        your gateway to the Consumer IoT Platform
                      </p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
              <div className="flex flex-col items-center justify-center content-center gap-2">
                <div className="mb-5 w-64">
                  <LoginButton type="button" onClick={handleLogin} disabled={isLoading}>
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Signing in...
                      </div>
                    ) : (
                      'Sign In'
                    )}
                  </LoginButton>
                </div>
                <div className={isLocalHostDevOrTest ? 'mb-5' : 'opacity-0 pointer-events-none h-0'}>
                  <StyledAuthenticator />
                </div>
              </div>
            </motion.div>
          </div >
        )
      }

      {/* Features Sections with enhanced styling */}
      <div className="flex flex-col bg-transparent" style={{ backgroundRepeat: 'repeat', backgroundSize: 'cover', backgroundAttachment: 'fixed' }}>
        {/* Device Management Section */}        <section className="py-12 relative overflow-hidden" style={{ background: 'linear-gradient(110deg, rgba(99, 179, 237, 0.3) 0%, rgba(236, 244, 252, 0.7) 100%)' }}>

          <div className="max-w-6xl mx-auto px-8 relative z-10">
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/3 flex justify-center mb-8 md:mb-0 transform transition-all duration-500 hover:scale-110">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-full shadow-lg flex items-center justify-center">
                  <DevicePhoneMobileIcon className="text-white h-[70px] w-[70px]" />
                </div>
              </div>
              <div className="md:w-2/3 md:pl-10">
                <h2 className="text-3xl font-bold mb-4 text-gray-800 border-b-2 border-blue-400 pb-2 inline-block">Device Management</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Access the telemetry data of your device to monitor its performance and identify any issues or anomalies in its operation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Asset Management Section */}
        <section className="py-12 relative overflow-hidden" style={{ background: 'linear-gradient(110deg, rgba(255, 255, 255, 0.6) 0%, rgba(199, 210, 254, 0.5) 100%)' }}>

          <div className="max-w-6xl mx-auto px-8 relative z-10">
            <div className="flex flex-col md:flex-row-reverse items-center">
              <div className="md:w-1/3 flex justify-center mb-8 md:mb-0 transform transition-all duration-500 hover:scale-110">
                <div className="bg-white p-6 rounded-full shadow-lg border-2 border-indigo-300 flex items-center justify-center">
                  <WifiIcon className="text-indigo-500 h-[70px] w-[70px]" />
                </div>
              </div>
              <div className="md:w-2/3 md:pr-10">
                <h2 className="text-3xl font-bold mb-4 text-gray-800 border-b-2 border-indigo-300 pb-2 inline-block">Asset Management</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Easily upload assets, update device firmware over the air, and track the progress of updates.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Message Simulation Section */}
        <section className="py-12 relative overflow-hidden" style={{ background: 'linear-gradient(110deg, rgba(104, 211, 145, 0.1) 0%, rgba(240, 253, 244, 0.95) 100%)' }}>

          <div className="max-w-6xl mx-auto px-8 relative z-10">
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/3 flex justify-center mb-8 md:mb-0 transform transition-all duration-500 hover:scale-110">
                <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-full shadow-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="text-white h-[70px] w-[70px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div className="md:w-2/3 md:pl-10">
                <h2 className="text-3xl font-bold mb-4 text-gray-800 border-b-2 border-green-400 pb-2 inline-block">Message Simulation</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Support onboarding and development for new projects with advanced message simulation capabilities.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Certificate Management Section */}
        <section className="py-12 relative overflow-hidden" style={{ background: 'linear-gradient(110deg, rgba(255, 255, 255, 0.5) 0%, rgba(251, 146, 60, 0.3) 100%)' }}>

          <div className="max-w-6xl mx-auto px-8 relative z-10">
            <div className="flex flex-col md:flex-row-reverse items-center">
              <div className="md:w-1/3 flex justify-center mb-8 md:mb-0 transform transition-all duration-500 hover:scale-110">
                <div className="bg-white p-6 rounded-full shadow-lg border-2 border-orange-400 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="text-orange-500 h-[70px] w-[70px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
              <div className="md:w-2/3 md:pr-10">
                <h2 className="text-3xl font-bold mb-4 text-gray-800 border-b-2 border-orange-400 pb-2 inline-block">Certificate Management</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Easily manage certificates credentials and quota of your projects.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Service Request Section */}
        <section className="py-12 relative overflow-hidden" style={{ background: 'linear-gradient(110deg, rgba(190, 24, 93, 0.08) 0%, rgba(253, 242, 248, 0.9) 100%)' }}>

          <div className="max-w-6xl mx-auto px-8 relative z-10">
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/3 flex justify-center mb-8 md:mb-0 transform transition-all duration-500 hover:scale-110">
                <div className="bg-gradient-to-br from-pink-500 to-pink-600 p-6 rounded-full shadow-lg flex items-center justify-center">
                  <HeroLinkIcon className="text-white h-[70px] w-[70px]" />
                </div>
              </div>
              <div className="md:w-2/3 md:pl-10">
                <h2 className="text-3xl font-bold mb-4 text-gray-800 border-b-2 border-pink-400 pb-2 inline-block">Service Request</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Direct links to ServiceNow tickets for a variety of existing service requests associated with the IoT Platform.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Power BI Reports Section */}
        <section className="py-12 relative overflow-hidden" style={{ background: 'linear-gradient(110deg, rgba(255, 255, 255, 1) 0%, rgba(249, 168, 212, 0.1) 100%)' }}>

          <div className="max-w-6xl mx-auto px-8 relative z-10">
            <div className="flex flex-col md:flex-row-reverse items-center">
              <div className="md:w-1/3 flex justify-center mb-8 md:mb-0 transform transition-all duration-500 hover:scale-110">
                <div className="bg-white p-6 rounded-full shadow-lg border-2 border-purple-300 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="text-purple-500 h-[70px] w-[70px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div className="md:w-2/3 md:pr-10">
                <h2 className="text-3xl font-bold mb-4 text-gray-800 border-b-2 border-purple-300 pb-2 inline-block">Power BI Reports</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Quick link to Nestlé Brands Power BI reports for insightful data analysis.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div >
  );
}
